import { RefObject, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { FOCUS_PARAM, FocusTarget, parseFocusParam } from "./focusTarget";

/** How long the flash ring stays on the focused element. */
export const FOCUS_FLASH_MS = 1300;

const FLASH_CLASSES = ["ring-2", "ring-brand-light", "ring-offset-2", "rounded-lg"];

/** The `?focus=` target of the current URL, and a way to consume it. */
export const useFocusTarget = (): { target: FocusTarget | null; clear: () => void } => {
	const [searchParams, setSearchParams] = useSearchParams();
	const raw = searchParams.get(FOCUS_PARAM);
	const target = useMemo(() => parseFocusParam(raw), [raw]);

	const clear = useCallback(() => {
		setSearchParams(
			(current) => {
				const next = new URLSearchParams(current);

				next.delete(FOCUS_PARAM);

				return next;
			},
			{ replace: true }
		);
	}, [setSearchParams]);

	return { target, clear };
};

/**
 * Scrolls the element into view and flashes it once when `isFocused` becomes
 * true, then consumes the focus param so a reload does not flash again.
 */
export const useFlashOnFocus = (
	ref: RefObject<HTMLElement>,
	isFocused: boolean,
	onDone: () => void
): void => {
	useEffect(() => {
		const element = ref.current;

		if (!isFocused || !element) {
			return;
		}

		element.scrollIntoView({ behavior: "smooth", block: "center" });
		element.classList.add(...FLASH_CLASSES);

		const timer = setTimeout(() => {
			element.classList.remove(...FLASH_CLASSES);
			onDone();
		}, FOCUS_FLASH_MS);

		return () => clearTimeout(timer);
		// The element identity and the flag are what matter; onDone is stable per page.
	}, [isFocused, ref.current]);
};
